import { useState, useEffect, useCallback } from 'react';
import { FlaskConical, Send, ThumbsUp, Trophy, Clock, Lock, ChevronDown, ChevronUp, Star, Flame, Target } from 'lucide-react';
import * as labService from '../../services/labService';
import { saveCustomChallenge } from '../../utils/userService';
import { PremiumGate } from '../shared/PremiumGate';

const CATEGORIES = ['social', 'creative', 'gym', 'money', 'chaos', 'anti-opt'];
const MIN_LEN = 15;
const MAX_LEN = 140;

// ─── Category Badge ───────────────────────────────────────────────────────────
const CatBadge = ({ category, small }) => {
  const c = labService.CATEGORY_COLORS[category] || { text: 'text-gray-400', bg: 'bg-gray-800', border: 'border-gray-600' };
  return (
    <span className={`${c.bg} ${c.text} ${c.border} border font-black uppercase tracking-wider px-2 py-0.5 ${small ? 'text-[10px]' : 'text-xs'}`}>
      {category}
    </span>
  );
};

// ─── CUSTOM DAILY TAB ─────────────────────────────────────────────────────────
const CustomDailyTab = ({ userId }) => {
  const [text,     setText]     = useState('');
  const [category, setCategory] = useState('social');
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState(false);
  const [loading,  setLoading]  = useState(false);

  const handleSet = async () => {
    const trimmed = text.trim();
    if (trimmed.length < MIN_LEN) { setError(`At least ${MIN_LEN} characters — make it specific`); return; }
    if (trimmed.length > MAX_LEN) { setError(`Max ${MAX_LEN} characters`); return; }
    if (!category) { setError('Pick a category'); return; }

    setLoading(true);
    setError('');

    const challenge = {
      text: trimmed,
      category,
      points: 5,
      createdAt: new Date().toISOString(),
    };

    try {
      // Save to Supabase
      if (userId) await saveCustomChallenge(userId, challenge);

      // localStorage backup
      localStorage.setItem('senditmaxx_custom_challenge', JSON.stringify(challenge));

      setText('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 6000);
    } catch {
      setError('Failed to save. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500 p-4">
        <h3 className="text-purple-400 font-black text-lg uppercase tracking-wide mb-1 flex items-center gap-2">
          <Target size={18} /> SET TOMORROW'S CHALLENGE
        </h3>
        <p className="text-gray-500 text-xs font-bold uppercase mb-4">
          WRITE YOUR OWN DAILY CHALLENGE — IT FIRES TOMORROW WHEN YOUR CURRENT ONE EXPIRES
        </p>

        <textarea
          value={text}
          onChange={e => { setText(e.target.value); setError(''); }}
          placeholder="Write your own challenge for tomorrow..."
          maxLength={MAX_LEN + 10}
          rows={3}
          className="w-full bg-black border-2 border-purple-700 text-white placeholder-gray-700 p-3 font-bold text-sm resize-none focus:outline-none focus:border-purple-500 mb-2"
        />

        <div className="flex justify-between text-xs font-bold mb-3">
          <span className={text.length < MIN_LEN ? 'text-gray-600' : text.length > MAX_LEN ? 'text-red-500' : 'text-purple-500'}>
            {text.length}/{MAX_LEN}
          </span>
        </div>

        {/* Category selector */}
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          {CATEGORIES.map(cat => {
            const c = labService.CATEGORY_COLORS[cat];
            const active = category === cat;
            return (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`py-2 font-black text-xs uppercase tracking-wide border transition-all touch-manipulation ${
                  active
                    ? `${c.bg} ${c.text} ${c.border}`
                    : 'bg-black text-gray-600 border-gray-800 hover:border-gray-600'
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {error && <p className="text-red-400 font-black text-xs uppercase mb-3">{error}</p>}

        {success && (
          <div className="bg-purple-900 border border-purple-500 p-3 mb-3 text-center">
            <p className="text-purple-300 font-black text-sm uppercase">🎯 LOCKED IN — YOUR CUSTOM CHALLENGE IS SET FOR TOMORROW</p>
          </div>
        )}

        <button
          onClick={handleSet}
          disabled={loading || text.trim().length < MIN_LEN || text.trim().length > MAX_LEN}
          className="w-full bg-purple-600 hover:bg-purple-500 active:scale-95 text-white py-4 font-black text-base uppercase tracking-wider border-2 border-purple-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-manipulation"
        >
          <Target size={16} />
          {loading ? 'SAVING...' : "SET AS TOMORROW'S CHALLENGE"}
        </button>
      </div>

      {/* Info box */}
      <div className="border border-gray-800 p-3">
        <p className="text-gray-600 text-xs font-black uppercase mb-2">HOW IT WORKS</p>
        <ul className="space-y-1">
          {[
            'Your custom challenge replaces tomorrow\'s random daily',
            'Counts toward your streak and earns full points',
            'Tagged with a 🎯 CUSTOM badge on the challenge card',
            'Only one pending custom challenge at a time',
          ].map((r, i) => (
            <li key={i} className="text-gray-600 text-xs font-bold flex items-start gap-1.5">
              <span className="text-purple-600 mt-0.5 flex-shrink-0">▸</span>{r}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ─── SUBMIT TAB ───────────────────────────────────────────────────────────────
// inner CommunitySubmitForm extracted so SubmitTab can wrap it cleanly
const CommunitySubmitForm = ({ userId, username, onSubmitted }) => {
  const [text,     setText]     = useState('');
  const [category, setCategory] = useState('social');
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loadingMine,   setLoadingMine]   = useState(true);

  const loadMine = useCallback(async () => {
    if (!userId) return;
    try {
      const subs = await labService.fetchMySubmissions(userId);
      setMySubmissions(subs);
    } catch { /* silent */ }
    finally { setLoadingMine(false); }
  }, [userId]);

  useEffect(() => { loadMine(); }, [loadMine]);

  const activeCount = mySubmissions.filter(s => s.status === 'voting').length;

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (trimmed.length < MIN_LEN) { setError(`At least ${MIN_LEN} characters — make it specific`); return; }
    if (trimmed.length > MAX_LEN) { setError(`Max ${MAX_LEN} characters`); return; }
    if (!category) { setError('Pick a category'); return; }
    if (activeCount >= 3) { setError('You already have 3 active submissions. Wait for one to be voted on.'); return; }

    setLoading(true);
    setError('');
    try {
      const sub = await labService.submitChallenge({ userId, username, text: trimmed, category });
      setText('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
      await loadMine();
      if (onSubmitted) onSubmitted(sub);
    } catch (err) {
      if (err.message === 'MAX_SUBMISSIONS_REACHED') {
        setError('Max 3 active submissions at once. Wait for yours to be decided.');
      } else {
        setError('Failed to submit. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    voting:   'text-yellow-400 border-yellow-600',
    promoted: 'text-green-400 border-green-600',
    rejected: 'text-gray-600 border-gray-700',
  };
  const statusLabels = {
    voting:   '🗳 VOTING',
    promoted: '✓ PROMOTED',
    rejected: '✗ NOT SELECTED',
  };

  return (
    <div className="space-y-4">
      {/* Submission Form */}
      <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-amber-500 p-4">
        <h3 className="text-amber-400 font-black text-lg uppercase tracking-wide mb-1">
          SUBMIT A CHALLENGE IDEA
        </h3>
        <p className="text-gray-500 text-xs font-bold uppercase mb-4">
          TOP VOTED PER CATEGORY GETS ADDED TO DAILY ROTATION — COMMUNITY PICK
        </p>

        <textarea
          value={text}
          onChange={e => { setText(e.target.value); setError(''); }}
          placeholder="Write a specific, doable challenge anyone can attempt today..."
          maxLength={MAX_LEN + 10}
          rows={3}
          className="w-full bg-black border-2 border-amber-700 text-white placeholder-gray-700 p-3 font-bold text-sm resize-none focus:outline-none focus:border-amber-500 mb-2"
        />

        <div className="flex justify-between text-xs font-bold mb-3">
          <span className={text.length < MIN_LEN ? 'text-gray-600' : text.length > MAX_LEN ? 'text-red-500' : 'text-amber-600'}>
            {text.length}/{MAX_LEN}
          </span>
          <span className="text-gray-600 uppercase">{activeCount}/3 ACTIVE</span>
        </div>

        {/* Category selector */}
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          {CATEGORIES.map(cat => {
            const c = labService.CATEGORY_COLORS[cat];
            const active = category === cat;
            return (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`py-2 font-black text-xs uppercase tracking-wide border transition-all touch-manipulation ${
                  active
                    ? `${c.bg} ${c.text} ${c.border}`
                    : `bg-black text-gray-600 border-gray-800 hover:border-gray-600`
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {error && <p className="text-red-400 font-black text-xs uppercase mb-3">{error}</p>}

        {success && (
          <div className="bg-green-900 border border-green-500 p-3 mb-3 text-center">
            <p className="text-green-400 font-black text-sm uppercase">✓ CHALLENGE SUBMITTED — VOTING OPENS NOW</p>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading || text.trim().length < MIN_LEN || text.trim().length > MAX_LEN}
          className="w-full bg-amber-500 hover:bg-amber-400 active:scale-95 text-black py-4 font-black text-base uppercase tracking-wider border-2 border-amber-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-manipulation"
        >
          <Send size={16} />
          {loading ? 'SUBMITTING...' : 'SUBMIT CHALLENGE'}
        </button>
      </div>

      {/* My Submissions */}
      {!loadingMine && mySubmissions.length > 0 && (
        <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 p-4">
          <h3 className="text-gray-400 font-black text-sm uppercase tracking-wide mb-3">MY SUBMISSIONS</h3>
          <div className="space-y-2">
            {mySubmissions.map(sub => (
              <div key={sub.id} className="bg-black border border-gray-800 p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-gray-300 text-sm font-bold leading-snug flex-1">{sub.text}</p>
                  <CatBadge category={sub.category} small />
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`text-xs font-black border px-2 py-0.5 uppercase ${statusColors[sub.status] || 'text-gray-600 border-gray-700'}`}>
                    {statusLabels[sub.status] || sub.status}
                  </span>
                  <span className="text-gray-600 text-xs font-bold">{sub.votes} VOTES</span>
                  {sub.status === 'promoted' && (
                    <span className="text-green-500 text-xs font-black">{sub.completion_count} COMPLETIONS</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rules */}
      <div className="border border-gray-800 p-3">
        <p className="text-gray-600 text-xs font-black uppercase mb-2">HOW IT WORKS</p>
        <ul className="space-y-1">
          {[
            'Submit a real, specific, doable challenge (15–140 chars)',
            'It enters a 48-hour voting window visible to all users',
            'Top voted per category gets promoted to daily rotation',
            'Max 3 active submissions at once',
            'Earn CHALLENGE MAKER badge when your submission is promoted',
            'Earn LEGEND MAKER when 100+ people complete your challenge',
          ].map((r, i) => (
            <li key={i} className="text-gray-600 text-xs font-bold flex items-start gap-1.5">
              <span className="text-amber-600 mt-0.5 flex-shrink-0">▸</span>{r}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ─── SUBMIT TAB WRAPPER (community + custom mode toggle) ─────────────────────
const SubmitTab = ({ userId, username, isPremium, onSubmitted, openUpgradeModal }) => {
  const [mode, setMode] = useState('community'); // 'community' | 'custom'

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="grid grid-cols-2 gap-1 border-2 border-gray-700">
        <button
          onClick={() => setMode('community')}
          className={`flex items-center justify-center gap-1.5 py-2.5 font-black text-xs uppercase tracking-wide transition-all touch-manipulation ${
            mode === 'community'
              ? 'bg-amber-500 text-black'
              : 'bg-black text-amber-600 hover:bg-amber-900'
          }`}
        >
          <Send size={12} /> COMMUNITY SUBMIT
        </button>
        <button
          onClick={() => setMode('custom')}
          className={`flex items-center justify-center gap-1.5 py-2.5 font-black text-xs uppercase tracking-wide transition-all touch-manipulation ${
            mode === 'custom'
              ? 'bg-purple-600 text-white'
              : 'bg-black text-purple-500 hover:bg-purple-900'
          }`}
        >
          <Target size={12} /> CUSTOM DAILY
          {!isPremium && <Lock size={10} className="ml-0.5" />}
        </button>
      </div>

      {mode === 'community' && (
        <CommunitySubmitForm userId={userId} username={username} onSubmitted={onSubmitted} />
      )}

      {mode === 'custom' && (
        <PremiumGate isPremium={isPremium} onUpgrade={openUpgradeModal} featureName="Custom Challenges">
          <CustomDailyTab userId={userId} />
        </PremiumGate>
      )}
    </div>
  );
};

// ─── VOTE TAB ─────────────────────────────────────────────────────────────────
const VoteTab = ({ userId, isPremium }) => {
  const [queue,      setQueue]      = useState([]);
  const [myVotes,    setMyVotes]    = useState(new Set());
  const [loading,    setLoading]    = useState(true);
  const [votingId,   setVotingId]   = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const [q, votes] = await Promise.all([
        labService.fetchVotingQueue(),
        userId ? labService.fetchMyVotes(userId) : Promise.resolve(new Set()),
      ]);
      setQueue(q);
      setMyVotes(votes);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const handleVote = async (sub) => {
    if (!isPremium) return; // gated — UI shows lock
    if (!userId || votingId) return;

    setVotingId(sub.id);
    const alreadyVoted = myVotes.has(sub.id);
    try {
      if (alreadyVoted) {
        await labService.removeVote(sub.id);
        setMyVotes(prev => { const n = new Set(prev); n.delete(sub.id); return n; });
        setQueue(prev => prev.map(s => s.id === sub.id ? { ...s, votes: Math.max(0, s.votes - 1) } : s));
      } else {
        await labService.castVote(sub.id);
        setMyVotes(prev => new Set([...prev, sub.id]));
        setQueue(prev => prev.map(s => s.id === sub.id ? { ...s, votes: s.votes + 1 } : s));
      }
    } catch { /* silent */ }
    finally { setVotingId(null); }
  };

  if (loading) return (
    <div className="text-center py-12">
      <p className="text-amber-400 font-black uppercase tracking-wide animate-pulse">LOADING QUEUE...</p>
    </div>
  );

  if (!queue.length) return (
    <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-gray-700 p-8 text-center">
      <FlaskConical size={32} className="text-gray-700 mx-auto mb-3" />
      <p className="text-gray-500 font-black uppercase tracking-wide">NO ACTIVE SUBMISSIONS</p>
      <p className="text-gray-700 text-xs font-bold uppercase mt-1">BE THE FIRST — SUBMIT ONE</p>
    </div>
  );

  // Group by category for display
  const byCategory = {};
  for (const sub of queue) {
    if (!byCategory[sub.category]) byCategory[sub.category] = [];
    byCategory[sub.category].push(sub);
  }

  return (
    <div className="space-y-3">
      {/* Premium gate notice */}
      {!isPremium && (
        <div className="bg-gradient-to-r from-amber-900 to-yellow-900 border border-amber-600 p-3 flex items-center gap-3">
          <Lock size={16} className="text-amber-400 flex-shrink-0" />
          <div>
            <p className="text-amber-300 font-black text-xs uppercase tracking-wide">VOTING IS PREMIUM</p>
            <p className="text-amber-600 text-xs font-bold">YOU CAN SEE THE QUEUE — PREMIUM UNLOCKS VOTING</p>
          </div>
        </div>
      )}

      {Object.entries(byCategory).map(([cat, subs]) => (
        <div key={cat} className="bg-gradient-to-br from-gray-900 to-black border border-gray-800">
          {/* Category header */}
          <div className={`px-3 py-2 border-b border-gray-800 flex items-center justify-between`}>
            <CatBadge category={cat} />
            <span className="text-gray-600 text-xs font-bold">{subs.length} SUBMISSION{subs.length !== 1 ? 'S' : ''}</span>
          </div>

          {/* Submissions in category */}
          <div className="divide-y divide-gray-900">
            {subs.map(sub => {
              const voted = myVotes.has(sub.id);
              const expanded = expandedId === sub.id;
              const isVoting = votingId === sub.id;

              return (
                <div key={sub.id} className="p-3">
                  <div className="flex items-start gap-3">
                    {/* Vote button */}
                    <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => isPremium ? handleVote(sub) : null}
                        disabled={isVoting}
                        className={`relative flex flex-col items-center justify-center w-12 h-12 border-2 font-black text-sm transition-all touch-manipulation ${
                          !isPremium
                            ? 'border-gray-800 text-gray-700 cursor-not-allowed bg-black'
                            : voted
                            ? 'border-amber-500 text-amber-400 bg-amber-900'
                            : 'border-gray-700 text-gray-500 hover:border-amber-500 hover:text-amber-400 bg-black'
                        }`}
                        title={!isPremium ? 'Premium feature' : voted ? 'Remove vote' : 'Vote'}
                      >
                        {!isPremium
                          ? <Lock size={14} className="text-gray-700" />
                          : <><ThumbsUp size={14} /><span className="text-xs">{sub.votes}</span></>
                        }
                      </button>
                      {isPremium && (
                        <span className="text-gray-700 text-[10px] font-black">{sub.votes}</span>
                      )}
                      {!isPremium && (
                        <span className="text-gray-700 text-[10px] font-black">{sub.votes}</span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-gray-200 text-sm font-bold leading-snug ${!expanded ? 'line-clamp-2' : ''}`}>
                        {sub.text}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-gray-600 text-xs font-bold">BY {sub.submitter_username?.toUpperCase()}</span>
                        <span className="text-gray-700">·</span>
                        <span className="flex items-center gap-0.5 text-gray-600 text-xs font-bold">
                          <Clock size={10} />{labService.timeUntilExpiry(sub.voting_ends_at)} LEFT
                        </span>
                      </div>
                    </div>

                    {/* Expand toggle */}
                    <button
                      onClick={() => setExpandedId(expanded ? null : sub.id)}
                      className="text-gray-700 hover:text-gray-500 flex-shrink-0 mt-0.5 touch-manipulation"
                    >
                      {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── HALL OF FAME TAB ─────────────────────────────────────────────────────────
const HallOfFameTab = () => {
  const [champs, setChamps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    labService.fetchHallOfFame(20).then(data => {
      setChamps(data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="text-center py-12">
      <p className="text-amber-400 font-black uppercase animate-pulse">LOADING HALL OF FAME...</p>
    </div>
  );

  if (!champs.length) return (
    <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-gray-700 p-8 text-center">
      <Trophy size={32} className="text-gray-700 mx-auto mb-3" />
      <p className="text-gray-500 font-black uppercase">NO COMMUNITY CHALLENGES YET</p>
      <p className="text-gray-700 text-xs font-bold uppercase mt-1">SUBMIT AND VOTE TO FILL THIS UP</p>
    </div>
  );

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div className="space-y-2">
      {/* Header card */}
      <div
        className="bg-gradient-to-r from-amber-800 to-yellow-800 border-2 border-amber-500 p-3 text-center skew-x-[-2deg]"
        style={{ boxShadow: '0 4px 20px rgba(245,158,11,0.4)' }}
      >
        <p className="text-amber-200 font-black text-lg uppercase tracking-tight skew-x-[2deg]">
          🏆 COMMUNITY HALL OF FAME 🏆
        </p>
        <p className="text-amber-500 text-xs font-bold uppercase skew-x-[2deg]">
          MOST COMPLETED COMMUNITY CHALLENGES
        </p>
      </div>

      {champs.map((sub, i) => {
        const isLegend = sub.completion_count >= 100;
        const rankNum = i + 1;

        return (
          <div
            key={sub.id}
            className={`bg-gradient-to-br from-gray-900 to-black border-2 p-4 transition-all ${
              rankNum === 1
                ? 'border-yellow-500'
                : rankNum === 2
                ? 'border-gray-400'
                : rankNum === 3
                ? 'border-orange-700'
                : 'border-gray-800'
            }`}
            style={rankNum <= 3 ? { boxShadow: rankNum === 1 ? '0 0 20px rgba(234,179,8,0.3)' : 'none' } : {}}
          >
            <div className="flex items-start gap-3">
              {/* Rank */}
              <div className="flex-shrink-0 flex flex-col items-center">
                <span className="text-2xl leading-none">{medals[i] || `#${rankNum}`}</span>
                {isLegend && <span className="text-yellow-400 text-[9px] font-black uppercase mt-0.5">LEGEND</span>}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold leading-snug mb-1.5">{sub.text}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <CatBadge category={sub.category} small />
                  <span className="text-gray-600 text-[10px] font-bold uppercase">by {sub.submitter_username}</span>
                </div>
              </div>

              {/* Completion count */}
              <div className="flex-shrink-0 text-right">
                <div className="flex items-center gap-1 justify-end">
                  <Flame size={14} className={isLegend ? 'text-yellow-400' : 'text-gray-500'} />
                  <span className={`font-black text-xl ${isLegend ? 'text-yellow-400' : 'text-gray-400'}`}>
                    {sub.completion_count}
                  </span>
                </div>
                <p className="text-gray-600 text-[10px] font-black uppercase">COMPLETIONS</p>
              </div>
            </div>

            {/* Legend banner */}
            {isLegend && (
              <div className="mt-2 bg-yellow-900 border border-yellow-600 px-2 py-1 text-center">
                <p className="text-yellow-400 text-xs font-black uppercase tracking-wide">
                  ⭐ LEGEND MAKER — {sub.submitter_username?.toUpperCase()}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── MAIN LAB VIEW ─────────────────────────────────────────────────────────────
export const LabView = ({ userId, username, isPremium, onSubmitted, openUpgradeModal }) => {
  const [tab, setTab] = useState('submit'); // 'submit' | 'vote' | 'hof'

  const tabs = [
    { id: 'submit', label: 'SUBMIT',       icon: Send   },
    { id: 'vote',   label: 'VOTE QUEUE',   icon: ThumbsUp },
    { id: 'hof',    label: 'HALL OF FAME', icon: Trophy },
  ];

  return (
    <div className="space-y-4 px-3 sm:px-4">
      {/* Header */}
      <div
        className="bg-gradient-to-r from-amber-600 to-orange-600 border-2 border-amber-400 p-4 sm:p-5 skew-x-[-2deg]"
        style={{ boxShadow: '0 4px 24px rgba(245,158,11,0.4)' }}
      >
        <div className="skew-x-[2deg] flex items-center gap-3">
          <FlaskConical size={28} className="text-black flex-shrink-0" />
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-black uppercase tracking-tight leading-none">
              CHALLENGE LAB
            </h2>
            <p className="text-amber-900 text-xs sm:text-sm font-black uppercase tracking-wider mt-0.5">
              COMMUNITY BUILT • COMMUNITY VOTED
            </p>
          </div>
          {isPremium && (
            <div className="ml-auto bg-black border border-amber-400 px-2 py-1">
              <div className="flex items-center gap-1">
                <Star size={10} className="text-amber-400" />
                <span className="text-amber-400 text-[10px] font-black uppercase">PREMIUM</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sub-navigation */}
      <div className="grid grid-cols-3 gap-1 border-2 border-amber-700">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex flex-col sm:flex-row items-center justify-center gap-1 py-2.5 sm:py-3 font-black text-[10px] sm:text-xs uppercase tracking-wide transition-all touch-manipulation ${
              tab === t.id
                ? 'bg-amber-500 text-black'
                : 'bg-black text-amber-600 hover:bg-amber-900'
            }`}
          >
            <t.icon size={14} />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'submit' && (
        <SubmitTab
          userId={userId}
          username={username}
          isPremium={isPremium}
          onSubmitted={onSubmitted}
          openUpgradeModal={openUpgradeModal}
        />
      )}
      {tab === 'vote' && (
        <VoteTab userId={userId} isPremium={isPremium} />
      )}
      {tab === 'hof' && (
        <HallOfFameTab />
      )}
    </div>
  );
};
